import {useCallback, useEffect, useState} from "react";
import {useAtomValue} from "jotai";
import {useResourceManagement} from "../hooks/resourceHooks";
import {swallowException} from "../utils/appUtils";
import {isResourceSucceeded} from "../utils/resourceUtils";
import {getInvitationsForUser} from "../services/invitationService";
import {UserInvitationEx} from "../types/domain/userInvitation";
import {activeUserLoadable} from "../store/activeUserStore";
import {getUserMemberships} from "../services/userService";
import {CompanyMemberItem} from "../types/companyMemberItem";
import {CompanyMemberEx} from "../types/domain/companyMember";

/**
 * Fetches the list of User Membership items, including Company Memberships and Invitations
 */
export const useActiveUserMembershipItems = () => {
    const [companyItems, setCompanyItems] = useState<CompanyMemberItem[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const userState = useAtomValue(activeUserLoadable);
    const user = userState.state === "hasData" && userState.data !== undefined ? userState.data : undefined;

    const membershipsManagement = useResourceManagement({getUserMemberships});
    const invitesManagement = useResourceManagement({getInvitationsForUser});
    const {getUserMemberships: getMemberships, resource: companies} = membershipsManagement;
    const {getInvitationsForUser: getInvitations, resource: invitations} = invitesManagement;

    const refresh = useCallback(() => {
        if (user) {
            getMemberships(user.id).catch(swallowException);
            getInvitations(user.id).catch(swallowException);
        }
    }, [getMemberships, getInvitations, user]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        if (user && (isResourceSucceeded(companies) || isResourceSucceeded(invitations))) {
            setCompanyItems([
                ...(isResourceSucceeded(companies)
                    ? companies.data.map((company) => companyMemberToMemberItem(company))
                    : []),
                ...(isResourceSucceeded(invitations) ? invitations.data.map(invitationToMemberItem) : []),
            ]);
            setIsLoading(false);
        }
    }, [companies, invitations, setCompanyItems, user, setIsLoading]);

    return {companyItems, isLoading, refresh};
};

const companyMemberToMemberItem = (member: CompanyMemberEx): CompanyMemberItem => ({
    membershipType: "Member",
    role: member.role,
    attributes: member.attributes,
    member,
});

const invitationToMemberItem = (invitation: UserInvitationEx): CompanyMemberItem => ({
    membershipType: "Invited",
    role: invitation.role,
    attributes: {},
    invitation,
});
